import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ErroApi } from '../lib/api';
import {
  apagarEvento,
  buscarAgenda,
  buscarLinkAgenda,
  CORES_AGENDA,
  corDoItem,
  criarEvento,
  diaDe,
  gerarLinkAgenda,
  gradeDoMes,
  horaDe,
  HOJE_SP,
  mesAtual,
  nomeDoMes,
  somarMeses,
  type ItemAgenda,
} from '../lib/agenda';
import { doCampoDataHora } from '../lib/quadro';
import { buscarEscalaCultos, type DiaCulto, type Demanda } from '../lib/escalaCultos';
import { Link } from 'react-router-dom';
import { Alerta, Botao, Cartao } from '../componentes/Interface';
import { useConfirmacao } from '../componentes/Confirmacao';
import Layout from '../componentes/Layout';
import NovoEvento from '../componentes/NovoEvento';
import RepeticaoSemanal from '../componentes/RepeticaoSemanal';

const DIAS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab'];

export default function Agenda() {
  const [mes, setMes] = useState(mesAtual());
  const [itens, setItens] = useState<ItemAgenda[]>([]);
  const [podeEditar, setPodeEditar] = useState(false);
  const [diaEscolhido, setDiaEscolhido] = useState(HOJE_SP());
  const [erro, setErro] = useState<string | null>(null);
  const [criando, setCriando] = useState(false);
  const [link, setLink] = useState<string | null>(null);
  const [copiado, setCopiado] = useState(false);
  const retrato = useRef('');
  const painelDoDia = useRef<HTMLDivElement>(null);
  const { confirmar, dialogo } = useConfirmacao();
  const [verRepeticoes, setVerRepeticoes] = useState(false);

  // escala dos cultos do mes: qual equipe cuida de cada demanda. So a lideranca
  // enxerga essa escala; para os outros o servidor responde 403 e o painel do dia
  // simplesmente nao mostra o bloco.
  const [escala, setEscala] = useState<{ dias: Map<string, DiaCulto>; demandas: Demanda[] } | null>(null);
  const [erroEscala, setErroEscala] = useState<string | null>(null);
  useEffect(() => {
    let vivo = true;
    const grade = gradeDoMes(mes);
    buscarEscalaCultos(grade[0]!.dia, grade[grade.length - 1]!.dia, 'todos')
      .then((r) => {
        if (!vivo) return;
        setEscala({ dias: new Map(r.dias.map((d) => [d.dia, d])), demandas: r.demandas });
        setErroEscala(null);
      })
      .catch((e) => {
        if (!vivo) return;
        setEscala(null);
        setErroEscala(e instanceof ErroApi && e.status === 403 ? null : 'Não consegui carregar a escala dos cultos.');
      });
    return () => {
      vivo = false;
    };
  }, [mes]);

  function escolherDia(dia: string) {
    setDiaEscolhido(dia);
    // no celular o painel do dia fica embaixo do calendario: leva a pessoa ate ele
    if (window.matchMedia('(max-width: 1023px)').matches) {
      setTimeout(() => painelDoDia.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
    }
  }

  const carregar = useCallback(
    async (silencioso = false) => {
      try {
        const d = await buscarAgenda(mes);
        const assinatura = JSON.stringify(d);
        // so redesenha quando mudou de verdade
        if (assinatura !== retrato.current) {
          retrato.current = assinatura;
          setItens(d.itens);
          setPodeEditar(d.podeEditar);
        }
        setErro(null);
      } catch (e) {
        if (!silencioso) setErro(e instanceof ErroApi ? e.message : 'Não consegui carregar a agenda.');
      }
    },
    [mes],
  );

  useEffect(() => {
    void carregar();
    const t = setInterval(() => void carregar(true), 60000);
    return () => clearInterval(t);
  }, [carregar]);

  useEffect(() => {
    buscarLinkAgenda()
      .then((d) => setLink(d.caminho))
      .catch(() => setLink(null));
  }, []);

  const porDia = useMemo(() => {
    const mapa = new Map<string, ItemAgenda[]>();
    for (const i of itens) {
      const dia = diaDe(i.inicio);
      const lista = mapa.get(dia) ?? [];
      lista.push(i);
      mapa.set(dia, lista);
    }
    return mapa;
  }, [itens]);

  const doDia = porDia.get(diaEscolhido) ?? [];
  const urlCompleta = link ? `${window.location.origin}${link}` : '';

  async function removerEvento(id: number, titulo: string) {
    const ok = await confirmar({
      titulo: 'Apagar este evento?',
      texto: `"${titulo}" sai da agenda de todo mundo, inclusive dos calendarios assinados.`,
      confirmar: 'Apagar',
    });
    if (!ok) return;
    try {
      await apagarEvento(id);
      retrato.current = '';
      await carregar();
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não consegui apagar.');
    }
  }

  return (
    <Layout
      titulo="Calendário"
      subtitulo="Eventos da igreja e entregas da equipe, no horário de Brasilia."
      acoes={
        podeEditar && (
          <Botao onClick={() => setCriando(true)}>+ Novo evento</Botao>
        )
      }
    >
      {erro && (
        <div className="mb-4 max-w-xl">
          <Alerta>{erro}</Alerta>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <div className="rounded-2xl bg-white p-4 shadow-cartao">
          <div className="mb-4 flex items-center justify-between">
            <button
              onClick={() => setMes(somarMeses(mes, -1))}
              className="rounded-lg px-3 py-1 text-tinta-400 hover:bg-tinta-100"
            >
              &larr;
            </button>
            <h2 className="font-semibold text-tinta-900">{nomeDoMes(mes)}</h2>
            <button
              onClick={() => setMes(somarMeses(mes, 1))}
              className="rounded-lg px-3 py-1 text-tinta-400 hover:bg-tinta-100"
            >
              &rarr;
            </button>
          </div>

          <div className="grid grid-cols-7 gap-1 text-center text-xs font-semibold uppercase text-tinta-400">
            {DIAS.map((d) => (
              <span key={d}>{d}</span>
            ))}
          </div>

          <div className="mt-1 grid grid-cols-7 gap-1">
            {gradeDoMes(mes).map(({ dia, doMes }) => {
              const eventos = porDia.get(dia) ?? [];
              const hoje = dia === HOJE_SP();
              const escolhido = dia === diaEscolhido;
              return (
                <button
                  key={dia}
                  onClick={() => escolherDia(dia)}
                  className={`min-h-[4.5rem] rounded-lg border p-1 text-left transition ${
                    escolhido
                      ? 'border-tinta-900 bg-tinta-900/5'
                      : 'border-transparent hover:border-tinta-200'
                  } ${doMes ? '' : 'opacity-40'}`}
                >
                  <span
                    className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-xs ${
                      hoje ? 'bg-tinta-900 font-bold text-white' : 'text-tinta-600'
                    }`}
                  >
                    {Number(dia.slice(8))}
                  </span>
                  <span className="mt-1 block space-y-0.5">
                    {eventos.slice(0, 2).map((e) => (
                      <span
                        key={`${e.tipo}-${e.id}`}
                        title={`${horaDe(e.inicio)} ${e.titulo}`}
                        className={`block h-1.5 truncate rounded sm:h-auto sm:px-1 sm:text-[10px] sm:font-medium ${corDoItem(e).etiqueta}`}
                      >
                        <span className="hidden sm:inline">
                          {horaDe(e.inicio)} {e.titulo}
                        </span>
                      </span>
                    ))}
                    {eventos.length > 2 && (
                      <span className="block px-1 text-[10px] text-tinta-400">
                        +{eventos.length - 2}
                      </span>
                    )}
                  </span>
                </button>
              );
            })}
          </div>

          {/* legenda das cores */}
          <div className="mt-4 flex flex-wrap gap-x-4 gap-y-2 border-t border-tinta-200 pt-3">
            {Object.values(CORES_AGENDA).map((c) => (
              <span key={c.rotulo} className="flex items-center gap-1.5 text-xs text-tinta-600">
                <span className={`inline-block h-3 w-3 rounded ${c.etiqueta}`} />
                {c.rotulo}
              </span>
            ))}
          </div>
        </div>

        <div className="scroll-mt-4 space-y-4" ref={painelDoDia}>
          <Cartao>
            <h3 className="font-semibold text-tinta-900">
              {new Intl.DateTimeFormat('pt-BR', {
                timeZone: 'UTC',
                day: '2-digit',
                month: 'long',
              }).format(new Date(`${diaEscolhido}T12:00:00Z`))}
            </h3>
            {doDia.length === 0 ? (
              <p className="mt-2 text-sm text-tinta-400">Nada marcado neste dia.</p>
            ) : (
              <ul className="mt-3 space-y-3">
                {doDia.map((e) => (
                  <li key={`${e.tipo}-${e.id}`} className={`border-l-4 pl-3 ${corDoItem(e).borda}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-tinta-900">{e.titulo}</p>
                        <p className="text-xs text-tinta-400">
                          {horaDe(e.inicio)} · {e.local ?? (e.tipo === 'entrega' ? 'Quadro da mídia' : 'Sem local')}
                        </p>
                        {e.minhaEscala && (
                          <span className="mt-1 inline-block rounded bg-tinta-900 px-2 text-xs text-white">
                            Voce esta escalado: {e.minhaEscala}
                          </span>
                        )}
                        {!e.publico && e.tipo === 'evento' && (
                          <span className="mt-1 ml-1 inline-block rounded bg-slate-200 px-2 text-xs text-tinta-600">
                            interno
                          </span>
                        )}
                        {e.tipo === 'evento' && escala && (
                          <EscalaDoCulto dia={escala.dias.get(diaEscolhido)} eventoId={e.id} demandas={escala.demandas} />
                        )}
                      </div>
                      {podeEditar && e.tipo === 'evento' && (
                        <button
                          onClick={() => void removerEvento(e.id, e.titulo)}
                          className="text-xs text-tinta-400 hover:text-red-600"
                        >
                          apagar
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
            {erroEscala && <p className="mt-3 text-xs text-amber-700">{erroEscala}</p>}
          </Cartao>

          {podeEditar &&
            (verRepeticoes ? (
              <div className="space-y-2">
                <button
                  onClick={() => setVerRepeticoes(false)}
                  className="px-1 text-sm font-medium text-tinta-400 hover:text-tinta-900"
                >
                  ▾ esconder cultos que se repetem
                </button>
                <RepeticaoSemanal aoMudar={() => void carregar()} />
              </div>
            ) : (
              <button
                onClick={() => setVerRepeticoes(true)}
                className="flex w-full items-center justify-between rounded-2xl bg-white p-4 text-left shadow-cartao hover:bg-tinta-100"
              >
                <span>
                  <span className="block font-semibold text-tinta-900">Cultos que se repetem</span>
                  <span className="block text-sm text-tinta-400">Culto da Fé, Arena, Domingo e o que mais for semanal</span>
                </span>
                <span className="text-tinta-400">▸</span>
              </button>
            ))}

          <Cartao>
            <h3 className="font-semibold text-tinta-900">Assinar no seu celular</h3>
            <p className="mt-1 text-sm text-tinta-600">
              Este link e so seu e mostra apenas o que voce pode ver.
            </p>
            <div className="mt-3 flex gap-2">
              <input
                readOnly
                value={urlCompleta}
                onFocus={(e) => e.currentTarget.select()}
                className="w-full rounded-xl border border-tinta-200 px-3 py-2 text-xs text-tinta-600"
              />
              <Botao
                variante="secundario"
                className="shrink-0 px-3 py-2 text-sm"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(urlCompleta);
                    setCopiado(true);
                    setTimeout(() => setCopiado(false), 2000);
                  } catch {
                    setErro('Não consegui copiar. Selecione o texto e copie na mao.');
                  }
                }}
              >
                {copiado ? 'Copiado' : 'Copiar'}
              </Botao>
            </div>

            <details className="mt-3 text-sm text-tinta-600">
              <summary className="cursor-pointer font-medium text-tinta-800">Como assinar</summary>
              <p className="mt-2">
                <b>iPhone:</b> Ajustes → Calendário → Contas → Adicionar → Outro → Assinar calendário
                → cole o link.
              </p>
              <p className="mt-2">
                <b>Google Agenda:</b> Outras agendas → A partir de URL → cole o link.
              </p>
            </details>

            <Botao
              variante="secundario"
              className="mt-3 w-full text-sm"
              onClick={async () => {
                const ok = await confirmar({
                  titulo: 'Gerar um link novo?',
                  texto: 'O link atual para de funcionar na hora. Você precisara assinar de novo no celular.',
                  confirmar: 'Gerar novo',
                });
                if (!ok) return;
                try {
                  const d = await gerarLinkAgenda();
                  setLink(d.caminho);
                } catch (e) {
                  setErro(e instanceof ErroApi ? e.message : 'Não consegui gerar o link.');
                }
              }}
            >
              Gerar novo link
            </Botao>
          </Cartao>
        </div>
      </div>

      {criando && (
        <NovoEvento
          diaSugerido={diaEscolhido}
          aoFechar={() => setCriando(false)}
          aoCriar={async () => {
            setCriando(false);
            retrato.current = '';
            await carregar();
          }}
        />
      )}
      {dialogo}
    </Layout>
  );
}

/** Qual equipe cuida de cada demanda naquele culto (no domingo, so as do turno dele). */
function EscalaDoCulto({ dia, eventoId, demandas }: { dia?: DiaCulto; eventoId: number; demandas: Demanda[] }) {
  if (!dia) return null;
  const culto = dia.cultos.find((c) => c.id === eventoId);
  if (!culto) return null;
  const nomeDe = new Map(demandas.map((d) => [d.chave, d.nome]));
  const ordem = new Map(demandas.map((d, i) => [d.chave, i]));
  const linhas = dia.demandas
    .map((d) => ({
      demanda: d.demanda,
      equipes: d.equipes.filter((e) => !e.turno || !culto.turno || e.turno === culto.turno),
    }))
    .filter((d) => d.equipes.length > 0)
    .sort((a, b) => (ordem.get(a.demanda) ?? 99) - (ordem.get(b.demanda) ?? 99));

  return (
    <div className="mt-2 rounded-xl bg-tinta-100/70 p-2">
      <div className="mb-1 flex items-center justify-between gap-2 px-1">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-tinta-400">Escala do culto</span>
        {culto.total > 0 && (
          <span className="text-[11px] text-tinta-400">
            {culto.confirmados}/{culto.total} confirmados
          </span>
        )}
      </div>
      {linhas.length === 0 ? (
        <p className="px-1 text-xs text-tinta-400">
          Nenhuma equipe escalada ainda.{' '}
          <Link to="/escala-cultos" className="font-medium text-tinta-900 underline">
            Montar escala
          </Link>
        </p>
      ) : (
        <ul className="divide-y divide-tinta-200">
          {linhas.map((l) => (
            <li key={l.demanda} className="flex items-baseline justify-between gap-3 px-1 py-1 text-xs">
              <span className="text-tinta-400">{nomeDe.get(l.demanda) ?? l.demanda}</span>
              <span className="text-right font-semibold text-tinta-900">{l.equipes.map((e) => e.nome).join(' / ')}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
